/**
 * AntAttachmentSection — Component chung cho danh sách đính kèm
 * Dùng ở: TaskPanel, NotesPanel, ChatPanel settings, AIPanel settings
 *
 * Props:
 *   docs: array — danh sách documents [{ id, name, file_name, full_path, url, sub_type, user_name, created_user_name, created_at }]
 *   loading: boolean
 *   onUpload: function(e) — file input onChange handler
 *   onDelete: function(doc) — xoá 1 doc
 *   onPreview: function(doc) — preview/open 1 doc
 *   fileInputRef: ref — ref cho hidden file input
 *   showAdd: boolean (default true)
 *   readOnly: boolean (default false — ẩn add + delete)
 */
import React from 'react';
import AntLocalization from '../../utils/AntLocalization';
import './index.scss';

function commentFormatTime(ts) {
    if (!ts) return '';
    var ms = ts < 1e12 ? ts * 1000 : ts;
    var diff = Math.floor((Date.now() - ms) / 1000);
    if (diff < 60) return AntLocalization.translateText({ vi: 'Vừa xong', en: 'Just now', fr: 'À l\'instant', es: 'Ahora', id: 'Baru saja', ja: 'たった今', zh: '刚刚', ko: '방금', hi: 'अभी', ar: 'الآن' });
    if (diff < 3600) return Math.floor(diff / 60) + AntLocalization.translateText({ vi: ' phút', en: 'm', fr: ' min', es: ' min', id: ' mnt', ja: '分', zh: '分钟', ko: '분', hi: ' मि.', ar: ' د' });
    if (diff < 86400) return Math.floor(diff / 3600) + AntLocalization.translateText({ vi: ' giờ', en: 'h', fr: ' h', es: ' h', id: ' jam', ja: '時間', zh: '小时', ko: '시간', hi: ' घं.', ar: ' س' });
    if (diff < 604800) return Math.floor(diff / 86400) + AntLocalization.translateText({ vi: ' ngày', en: 'd', fr: ' j', es: ' d', id: ' hari', ja: '日', zh: '天', ko: '일', hi: ' दिन', ar: ' يوم' });
    var d = new Date(ms);
    return d.toLocaleDateString('vi-VN');
}

function getFileIcon(name) {
    var ext = (name || '').split('.').pop().toLowerCase();
    var isImage = /^(jpg|jpeg|png|gif|webp|bmp|svg)$/.test(ext);
    if (isImage) return '🖼️';
    if (ext === 'pdf') return '📄';
    if (ext === 'doc' || ext === 'docx') return '📝';
    if (ext === 'xls' || ext === 'xlsx') return '📊';
    if (ext === 'mp4' || ext === 'mov' || ext === 'webm') return '🎬';
    if (ext === 'zip' || ext === 'rar') return '📦';
    if (ext === 'mp3' || ext === 'wav') return '🎵';
    return '📎';
}

export default function AntAttachmentSection(props) {
    var docs = props.docs || [];
    var loading = props.loading;
    var readOnly = props.readOnly;
    var showAdd = props.showAdd !== false && !readOnly;
    var fileInputRef = props.fileInputRef;

    return (
        <div className="ant-attachment-section">
            {/* Header */}
            <div className="ant-att-header">
                <span className="ant-att-label">
                    {AntLocalization.translateText({ vi: 'Tài liệu đính kèm', en: 'Attachments', fr: 'Pièces jointes', es: 'Adjuntos', id: 'Lampiran', ja: '添付ファイル', zh: '附件', ko: '첨부 파일', hi: 'अनुलग्नक', ar: 'المرفقات' })}
                    {docs.length > 0 && <span className="ant-att-count"> ({docs.length})</span>}
                </span>
                {showAdd && (
                    <button className="ant-att-add-btn" onClick={function () { if (fileInputRef && fileInputRef.current) fileInputRef.current.click(); }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" /></svg>
                        {AntLocalization.translateText({ vi: 'Thêm', en: 'Add', fr: 'Ajouter', es: 'Añadir', id: 'Tambah', ja: '追加', zh: '添加', ko: '추가', hi: 'जोड़ें', ar: 'إضافة' })}
                    </button>
                )}
                {showAdd && fileInputRef && (
                    <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={props.onUpload} />
                )}
            </div>

            {/* List */}
            <div className="ant-att-list">
                {loading && <div className="ant-att-empty">{AntLocalization.translateText({ vi: 'Đang tải...', en: 'Loading...', fr: 'Chargement...', es: 'Cargando...', id: 'Memuat...', ja: '読み込み中...', zh: '加载中...', ko: '로딩 중...', hi: 'लोड हो रहा...', ar: 'تحميل...' })}</div>}
                {!loading && docs.length === 0 && <div className="ant-att-empty">{AntLocalization.translateText({ vi: 'Chưa có tài liệu', en: 'No documents', fr: 'Aucun fichier', es: 'Sin archivos', id: 'Belum ada file', ja: 'ファイルなし', zh: '暂无文件', ko: '파일 없음', hi: 'कोई फ़ाइल नहीं', ar: 'لا توجد ملفات' })}</div>}
                {docs.map(function (d) {
                    var name = d.name || d.file_name || 'File';
                    var url = d.full_path || d.url || d.file_url || null;
                    var isImage = url && /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(name);
                    var icon = getFileIcon(name);
                    var userName = d.user_name || d.created_user_name || '';
                    return (
                        <div key={d.id} className="shared-file-item">
                            <div className="shared-file-thumb" onClick={function () { if (props.onPreview) props.onPreview(d); }}>
                                {isImage && url ? <img src={url} alt="" /> : <span className="shared-file-icon">{icon}</span>}
                            </div>
                            <div className="shared-file-info">
                                <div className="shared-file-name" onClick={function () { if (props.onPreview) props.onPreview(d); }} title={name}>
                                    {name}
                                </div>
                                {(userName || d.created_at) && (
                                    <div className="shared-file-meta">
                                        {userName && <span>{userName}</span>}
                                        {d.created_at && <span>{commentFormatTime(d.created_at)}</span>}
                                    </div>
                                )}
                            </div>
                            <div className="shared-file-actions">
                                {url && (
                                    <a className="shared-file-btn" href={url} target="_blank" rel="noopener noreferrer"
                                        title={AntLocalization.translateText({ vi: 'Tải xuống', en: 'Download', fr: 'Télécharger', es: 'Descargar', id: 'Unduh', ja: 'ダウンロード', zh: '下载', ko: '다운로드', hi: 'डाउनलोड', ar: 'تنزيل' })}
                                        onClick={function (e) { e.stopPropagation(); }}>
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>
                                    </a>
                                )}
                                {!readOnly && props.onDelete && (
                                    <button className="shared-file-btn shared-file-del"
                                        onClick={function (e) { e.stopPropagation(); props.onDelete(d); }}
                                        title={AntLocalization.translateText({ vi: 'Xóa', en: 'Delete', fr: 'Supprimer', es: 'Eliminar', id: 'Hapus', ja: '削除', zh: '删除', ko: '삭제', hi: 'हटाएं', ar: 'حذف' })}>
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
